import { Divider, Form } from 'antd';
import { useCallback, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ButtonIconGhostMedium from '../../../../components/common/ButtonIconGhost/ButtonIconGhostMedium';
import ButtonPrimaryMedium from '../../../../components/common/ButtonPrimary/ButtonPrimaryMedium';
import InputComponent from '../../../../components/common/InputComponent/InputComponent';
import Tooltip from '../../../../components/common/Tooltip/Tooltip';
import { Icon } from '../../../../components/icon/icon';
import { ShortcutUtils } from '../../../../utils/shortcuts/ShortcutUtils';
import { cn } from '../../../../utils/string/cn.utils';
import { getInputType } from '../../../../utils/string/inputExpression';
import { DesignerPageContext } from '../../DesignerPage';
import './ToolbarDesign.scss';
import ButtonSecondaryMedium from '../../../../components/common/ButtonSecondary/ButtonSecondaryMedium';
import DimensionIcon from '../../../../components/dimension/DimensionIcon';
import { toggleDimensionEnabled } from '../../../../store/reducers/dimension.reducer';

const ToolbarDesign = ({
  selectedModel,
  selectedModels = [],
  sceneManager,
  isDimensioning,
  setIsDimensioning,
  isOpenDoor,
  setIsOpenDoor,
  isHideDoor,
  setIsHideDoor,
  updateCabinetTrimSettings,
}) => {
  const { cbTrim, setCbTrim } = useContext(DesignerPageContext);

  const [openSettings, setOpenSettings] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isDimEnabled = useSelector(state => state.dimension.enabled);

  const [form] = Form.useForm();

  const changeTimerRef = useRef(null);
  const [newCbTrim, setNewCbTrim] = useState(cbTrim);

  const validatorSize = ({ value, min, max }) => {
    if (value === '' || value === null || value === undefined) {
      return Promise.resolve();
    }

    const type = getInputType(value);

    if (type === 'text') {
      return Promise.reject(new Error(t('Invalid size')));
    }

    let num;
    if (type === 'expression') {
      num = evaluateExpression(value) / 1000;
    } else {
      num = parseFloat(value) / 1000;
    }

    if (isNaN(num)) {
      return Promise.reject(new Error(t('Invalid size')));
    }

    if (min !== undefined && max !== undefined) {
      if (num < min || num > max) {
        return Promise.reject(
          new Error(`${min * 1000}mm ${t('to')} ${max * 1000}mm`)
        );
      }
    } else {
      if (num <= 0) {
        return Promise.reject(new Error(t('Invalid size')));
      }
    }

    return Promise.resolve();
  };

  const debouncedValidate = useCallback(
    (changedValues, delay = 300) => {
      if (changeTimerRef.current) clearTimeout(changeTimerRef.current);

      changeTimerRef.current = setTimeout(async () => {
        const name = Object.keys(changedValues)[0];
        const value = changedValues[name];
        if (getInputType(value) === 'number') {
          try {
            await form.validateFields([name]);

            if (name === 'cbTrimWidth') {
              setNewCbTrim(prev => ({ ...prev, width: value / 1000 }));
            } else if (name === 'cbTrimHeight') {
              setNewCbTrim(prev => ({ ...prev, height: value / 1000 }));
            }
          } catch {
            // ignore
          }
        }
      }, delay);
    },
    [form]
  );

  const handleSaveSettings = () => {
    setCbTrim(newCbTrim);
    setOpenSettings(false);

    sceneManager?.createCabinetTrim?.updateCabinetTrimSettings(newCbTrim);
    sceneManager?.nestingController?.updateCabinetTrimSettings(newCbTrim);
  };

  const items = [
    updateCabinetTrimSettings && {
      icon: <Icon.ICSetting />,
      onClick: () => {
        setNewCbTrim(cbTrim);

        setOpenSettings(!openSettings);
      },
      tooltip: t('Settings'),
    },
    {
      icon: <Icon.ICCameraCenter />,
      onClick: () => {
        if (sceneManager) {
          sceneManager?.resetCamera?.();
        }
      },
      tooltip: t('Reset camera') + ' [C]',
    },
    {
      icon: <Icon.ICDoorSplash />,
      className: isHideDoor ? 'active' : '',
      onClick: () => {
        setIsHideDoor(prev => {
          if (sceneManager) {
            ShortcutUtils.visibleDoor(prev, isOpenDoor, sceneManager);

            return !prev;
          }
        });
      },
      tooltip: t('Hide/Show doors') + ' [H]',
    },
    {
      icon: <Icon.ICRuler />,
      className: isDimensioning ? 'active' : '',
      onClick: () => {
        setIsDimensioning(prev => {
          if (sceneManager)
            if (prev) {
              sceneManager.clearAllCornerPoints();
            } else {
              if (selectedModels.length) {
                selectedModels.forEach(item => {
                  sceneManager.generateCornerPoints(item);
                });
              } else {
                sceneManager.generateCornerPoints();
              }
            }
          return !prev;
        });
      },
      tooltip: t('Measure dimensions') + ' [Alt + D]',
    },
    {
      icon: <DimensionIcon />,
      className: isDimEnabled ? 'active' : '',
      onClick: () => dispatch(toggleDimensionEnabled()),
      tooltip: t('Dimension tool [DIM]'),
    },
    {
      icon: <Icon.ICMeasure />,
      onClick: () => {
        sceneManager?.positionController?.createDistanceLines?.();
        // sceneManager.positionController.visualizeDecomposition("module1");
      },
      disabled: selectedModel === null,
      tooltip: t('Measure distance to wall') + ' [Alt + Q]',
    },
    // {
    //   icon: <Icon.ICNews />,
    //   onClick: () => {
    //     sceneManager?.positionController?.getSelectedModuleDistances?.();
    //   },
    //   disabled: selectedModel === null,
    //   tooltip: t('Measure distance to wall') + ' [Alt + Q]',
    // },
    // {
    //   icon: <Icon.ICLoading />,
    //   onClick: () => {
    //     sceneManager?.positionController?.rotateSelectedModule180?.();
    //   },
    //   disabled: selectedModel === null,
    //   tooltip: t('Measure distance to wall') + ' [Alt + Q]',
    // },
  ].filter(Boolean);

  return (
    <div className="flex p-8 items-center gap-8 rounded-[8px] absolute left-0 bottom-0 w-fit">
      {items.map((item, index) => (
        <div key={index} className="relative">
          <Tooltip title={item.tooltip} placement="top">
            <ButtonIconGhostMedium
              icon={item.icon}
              onClick={item.onClick}
              className={cn(item.className, 'bg-[#fff] shadow-dropshadow8')}
              disabled={item.disabled}
            />
          </Tooltip>
        </div>
      ))}

      {openSettings && (
        <div className="setting-popup">
          <div
            className="setting-popup-overlay"
            onClick={() => setOpenSettings(false)}
          ></div>

          <div className="setting-popup-main">
            <Form
              labelCol={{ flex: '140px' }}
              labelAlign="left"
              labelWrap
              colon={false}
              layout="horizontal"
              initialValues={{
                cbTrimWidth: newCbTrim.width * 1000,
                cbTrimHeight: newCbTrim.height * 1000,
              }}
              onValuesChange={changedValues => debouncedValidate(changedValues)}
              itemGap={16}
            >
              <Form.Item
                label={
                  <span className="text-label-large">
                    {t('Cabinet trim width')}
                  </span>
                }
                name="cbTrimWidth"
                rules={[
                  {
                    validator: (_, value) =>
                      validatorSize({ value, min: 0.01, max: 0.3 }),
                  },
                ]}
                style={{ alignSelf: 'stretch', marginBottom: 8 }}
              >
                <InputComponent
                  disabled={!updateCabinetTrimSettings}
                  size="large"
                  addonAfter={<span className="text-body-medium">mm</span>}
                  placeholder={t('Width')}
                />
              </Form.Item>

              <Form.Item
                label={
                  <span className="text-label-large">
                    {t('Cabinet trim height')}
                  </span>
                }
                name="cbTrimHeight"
                rules={[
                  {
                    validator: (_, value) =>
                      validatorSize({ value, min: 0.01, max: 0.5 }),
                  },
                ]}
                style={{ alignSelf: 'stretch' }}
              >
                <InputComponent
                  disabled={!updateCabinetTrimSettings}
                  size="large"
                  addonAfter={<span className="text-body-medium">mm</span>}
                  placeholder={t('Height')}
                />
              </Form.Item>

              <Divider type="horizontal" className="my-[12px]" />

              <div className="flex justify-end">
                <ButtonPrimaryMedium
                  disabled={!updateCabinetTrimSettings}
                  title={t('Save')}
                  onClick={handleSaveSettings}
                />
              </div>
            </Form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ToolbarDesign;
